import getUnixTime from 'date-fns/getUnixTime';

const baseUrl = import.meta.env.VITE_APP_BASE_URL;
const railsBaseUrl = import.meta.env.VITE_RAILS_BASE_URL;
const v3BaseUrl = import.meta.env.VITE_V3_BASE_URL;
const exerciseBaseUrl = import.meta.env.VITE_EXERCISE_BASE_URL;

// Helper to make any relative URL absolute using current domain
const makeAbsolute = (url) => {
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`;
};

export const externalLinks = {
  base: `${baseUrl}`,
  railsBase: `${railsBaseUrl}`,
  home: `${railsBaseUrl}/`,
  trainerDashboard: `${railsBaseUrl}/dashboard/trainer-dashboard/`,
  notifications: `${railsBaseUrl}/dashboard/me/notifications/`,
  billing: `${baseUrl}/me/account/`,
  support: `${railsBaseUrl}/dashboard/trainer/support/`,
  signOut: `${railsBaseUrl}/users/sign_out/`,
  login: `${baseUrl}/login/`,
  connectedApps: `${railsBaseUrl}/dashboard/me/apps/`,
  loggedWorkouts: `${railsBaseUrl}/dashboard/workouts/`,
  activity: `${railsBaseUrl}/dashboard/activity/`,
  liveReports: `${baseUrl}/reports/?page=1&search=Real-Time`,
  measurements: `${railsBaseUrl}/dashboard/health/`,
  schedule: `${baseUrl}/embed/fbm/schedule/`,
  // Helper function to make any link absolute using current domain
  absolute: makeAbsolute,

  v3Url: (url) => {
    return `${v3BaseUrl}${url}`;
  },

  v2Url: (url) => {
    return `${railsBaseUrl}${url}`;
  },

  platformAdmin: (platformId) => {
    const mode = import.meta.env.MODE;
    const id = platformId ? `${platformId}/details` : '';

    if (mode === 'development') {
      return `http://localhost:5001/ex4/admin/platform/${id}/`;
    } else if (mode === 'staging') {
      return `https://staging.exercise.com/ex4/admin/platform/${id}/`;
    } else {
      return `https://www.exercise.com/ex4/admin/platform/${id}/`;
    }
  },

  accountManager: () => {
    return `${railsBaseUrl}/account-manager`;
  },

  searchUsers: (trainerId) => {
    return `${baseUrl}/admin/user?f_trainerId=${trainerId}`;
  },

  newLifecycleStage: () => {
    return `${railsBaseUrl}/dashboard/trainer/lifecycle/new`;
  },
  editLifecycleStage: (lifecycleStageId) => {
    return `${railsBaseUrl}/dashboard/trainer/lifecycle/${lifecycleStageId}`;
  },

  viewExercise: (exerciseId) => {
    return `${railsBaseUrl}/exercises/${exerciseId}`;
  },
  editExercise: (exerciseId) => {
    return `${railsBaseUrl}/dashboard/exercises/${exerciseId}`;
  },
  newExercise: () => {
    return `${railsBaseUrl}/dashboard/exercises/new`;
  },

  v2EditRoutine: (routineId) => `${railsBaseUrl}/v2/routine/${routineId}/edit/`,

  newSchedule: () => `${baseUrl}/embed/fbm/schedules/new`,
  editSchedule: (scheduleId) => `${baseUrl}/embed/fbm/schedules/${scheduleId}`,

  viewGroup: (cachedSlug, id) => {
    return `${railsBaseUrl}/groups/${cachedSlug || id}`;
  },

  newGroup: () => {
    return `${baseUrl}/groups/new`;
  },
  viewGroups: () => {
    return `${baseUrl}/groups`;
  },
  editGroup: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}`;
  },
  viewGroupVideos: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/videos`;
  },
  viewGroupResources: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/resources`;
  },
  viewGroupAssignments: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/assessments`;
  },
  viewGroupCalendar: (groupId) => {
    return `${v3BaseUrl}/calendar/group/${groupId}`;
  },
  manageGroupVideos: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/videos`;
  },
  manageGroupResources: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/resources`;
  },
  manageGroupAssignments: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/assessments`;
  },
  landingPage: (trainerId) => {
    return `${railsBaseUrl}/trainers/${trainerId}/landing/`;
  },
  assessmentsPage: () => {
    return `${railsBaseUrl}/dashboard/trainer/assessments`;
  },
  sequencesPage: () => {
    return `${railsBaseUrl}/dashboard/trainer/assessments/sequences`;
  },
  servicesPage: () => {
    return `${railsBaseUrl}${baseUrl}/embed/fbm/services`;
  },
  servicesEdit: (serviceId) => {
    return `${railsBaseUrl}/fbm/services/${serviceId}`;
  },

  newClient: (searchParams) => {
    return `${railsBaseUrl}/dashboard/clients/new${
      searchParams ? `?${searchParams}` : ''
    }`;
  },
  clientProfile: (userId) => `${railsBaseUrl}/users/${userId}/`,
  clientManagerLogin: (clientId) =>
    `${railsBaseUrl}/users/manager_login/?id=${clientId}`,
  clientBooking: (clientId) => `${railsBaseUrl}/a/booking?userId=${clientId}`,
  booking: () => `${railsBaseUrl}/booking`,
  clientCalendar: (clientId) => `${v3BaseUrl}/calendar/client/${clientId}/`,
  clientJournal: (clientId, userId) =>
    `${railsBaseUrl}/dashboard/journal/day/?client_id=${clientId}&other-client-id=${clientId}&user_id=${userId}`,
  clientJournalWorkout: (userId, workoutDate) =>
    `${railsBaseUrl}/dashboard/journal/day/?date=${getUnixTime(
      new Date(workoutDate)
    )}&user_id=${userId}`,

  clientMeasurements: (clientId, userId) =>
    `${railsBaseUrl}/dashboard/health/?client_id=${clientId}&user_id=${userId}`,
  clientSnapshot: (clientId, userId) =>
    `${railsBaseUrl}/dashboard/snapshot/?client_id=${clientId}&user_id=${userId}`,
  clientPerformance: (clientId, userId) =>
    `${railsBaseUrl}/dashboard/performance/?client_id=${clientId}&user_id=${userId}`,

  manageClient: (clientId) =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/info/`,
  manageClientPayment: (clientId) =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/payment/`,
  manageClientPackages: (clientId) =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/packages/`,
  manageClientDependents: (clientId) =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/dependents/`,
  manageClientSignatures: (clientId) =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/signatures/`,
  manageClientVisits: (clientId) =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/visits/`,
  manageClientTrainingInfo: (clientId) =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/training/`,

  manageCustomMeasurements: () =>
    `${railsBaseUrl}/dashboard/trainer/custom/?curTab=client#custom-client-measurements`,

  manageCustom: () => `${railsBaseUrl}/dashboard/trainer/custom/`,

  visitPaymentUrl: (visitId) =>
    `${railsBaseUrl}/dashboard/journal/day?date=${getUnixTime(
      new Date()
    )}&visit_id=${visitId}`,

  adminLogin: (trainerId, host, subdomain) => {
    const path = `/users/${trainerId}/login`;
    let protocol = 'https';

    if (import.meta.env.DEV) {
      host += ':3000';
      protocol = 'http';
    }

    if (host) {
      return `${protocol}://${host}${path}`;
    } else if (subdomain) {
      return `${protocol}://${subdomain}.exercise.com${path}`;
    } else {
      return `${railsBaseUrl}${path}`;
    }
  },

  legacyAdminEdit: (trainerId) =>
    `${exerciseBaseUrl}/dashboard/users/edit/${trainerId}/?curTab=admin`,

  printBarcodes: (userIds) =>
    `${v3BaseUrl}/print-barcodes?chrome=false&clients=${userIds}`,

  report: (report, queryParams) =>
    `${exerciseBaseUrl}/api/v4/reports/${report}.csv/${
      queryParams
        ? `?${Object.keys(queryParams)
            .map((key) => `${key}=${queryParams[key]}`)
            .join('&')}`
        : ''
    }`,

  /** @typedef { object } ReportParams
   *  @prop { number } start_date
   *  @prop { string } start_date_string
   *  @prop { number } end_date
   *  @prop { string } end_date_string
   *  @prop { 'month' } time_interval
   *  @prop { 'week' } rangeType
   *  @prop { number } page
   *  @prop { number } per
   *  @prop { string } trainer_id
   *  @prop { string } task_id
   */
  /** @type {(report: 'time_clock_entries', params: Partial<ReportParams>) => string} */
  reportUrl: (report, params) => {
    const urlParams = Object.keys(params)
      .map((key) => {
        const value = params[key];
        return `${key}=${value}`;
      })
      .join('&');

    return `${railsBaseUrl}/api/v4/reports/${report}.csv/?type=${report}&fetch_all=true&${urlParams}`;
  },

  contractSignatureUrl: (contractId) =>
    `${railsBaseUrl}/a/contract/${contractId}/`,

  exportClientMeasurements: (clientIds, startDate, endDate, stats) =>
    `${railsBaseUrl}/api/v2/clients/export_measurements?client_ids=${clientIds}${
      startDate ? `&start_date=${startDate}` : ''
    }${endDate ? `&end_date=${endDate}` : ''}${stats ? `&stats=${stats}` : ''}`,

  exportClientAccounts: (clientIds) =>
    `${railsBaseUrl}/api/v2/clients/export_accounts?client_ids=${clientIds}`,

  receiptUrl: (userId, hashId, chargeId) =>
    `${railsBaseUrl}/receipts/${userId}/transaction_receipt.pdf/?receipt=${hashId}&charge=${chargeId}`,

  stripeCustomer: (userId, platformUser = false) =>
    `${exerciseBaseUrl}/users/${userId}/stripe/${
      platformUser ? '' : '?stripe_customer_token=true'
    }`,

  editRoutine: (routineId, isRedesignBeta) => {
    return isRedesignBeta
      ? `${railsBaseUrl}/a/routines/${routineId}/edit`
      : `${railsBaseUrl}/routine/${routineId}`;
  },
  newRoutine: (isRedesignBeta) => {
    return isRedesignBeta
      ? `${railsBaseUrl}/a/routines/new`
      : `${railsBaseUrl}/v2/routines/new`;
  },

  workoutPlanFinder: () => {
    return `${railsBaseUrl}/workout-plans/`;
  },

  workoutPlanCalendar: (workoutPlanId, clientId) =>
    `${railsBaseUrl}/v2/workout-plan/${workoutPlanId}/calendar/${
      clientId ? `?client_id=${clientId}` : ''
    }`,

  editWorkoutPlan: (workoutPlanId) =>
    `${railsBaseUrl}/a/plans/${workoutPlanId}/edit/`,

  viewWorkoutPlan: (workoutPlanId) =>
    `${railsBaseUrl}/workout-plans/${workoutPlanId}/`,

  viewGroupUrl: (cachedSlug) => {
    return `${railsBaseUrl}/groups/${cachedSlug}`;
  },

  newWorkoutPlan: () => {
    return `${railsBaseUrl}/v2/workout-plans/new/`;
  },

  newWeekWorkoutPlan: () => {
    return `${railsBaseUrl}/a/plans/new/`;
  },

  newFlexWorkoutPlan: () => {
    return `${railsBaseUrl}/a/plans/new/?type=flex`;
  },

  createNewGroup: () => {
    return `${railsBaseUrl}/dashboard/groups/new`;
  },

  createNewResource: () => {
    return `${railsBaseUrl}/dashboard/trainer/resources/new`;
  },

  bookingPackages: () => {
    return `${v3BaseUrl}/packages`;
  },

  bookingHistory: () => {
    return `${baseUrl}/me/visits/`;
  },

  viewReceipt: (userId, hashId, chargeId, stripeObjectId) =>
    `${railsBaseUrl}/receipts/${userId}/transaction_receipt.pdf/?receipt=${hashId}&${
      stripeObjectId ? `stripe_id=${stripeObjectId}` : `charge=${chargeId}`
    }`,

  cancelSubscription: (sub, currentUser) =>
    `${railsBaseUrl}/leaving/?subscription_id=${sub.subId}?request_from_app=true&auth_token=${currentUser.authToken}`,

  cancelAllSubscriptions: () => `${railsBaseUrl}/leaving/`,

  reactivateSubscription: (sub) =>
    `${railsBaseUrl}/reupgrade/?subscription_id=${sub.subId}`,

  security: () => {
    return `${railsBaseUrl}/embed/dashboard/me/security/`;
  },

  groupJoin: (groupId) => {
    return `${railsBaseUrl}/groups/${groupId}/join/`;
  },

  stripeSubscriptions: () => {
    return `${railsBaseUrl}/dashboard/trainer/stripe/subscriptions`;
  },

  manageLocations: () => {
    return `${baseUrl}/fbm/locations`;
  },

  addGroupVideo: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/videos/new`;
  },

  editGroupVideos: (groupId, videoId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/videos/${videoId}`;
  },

  addGroupResource: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/resources/new`;
  },

  editGroupResources: (groupId, resourceId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/resources/${resourceId}`;
  },

  addGroupAssessments: (groupId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/assessments/new`;
  },

  editGroupAssessments: (groupId, assessmentId) => {
    return `${railsBaseUrl}/dashboard/groups/${groupId}/assessments/${assessmentId}`;
  },

  editGroupAssignment: (assessmentId) => {
    return `${railsBaseUrl}/dashboard/trainer/assessments/${assessmentId}`;
  },

  viewProduct: (productId) => {
    return `${railsBaseUrl}/products/${productId}`;
  },

  newTrainer: () => {
    return `${railsBaseUrl}/dashboard/trainer/trainers/new`;
  },

  editTrainer: (trainerId, tab = 'profile') => {
    return `${railsBaseUrl}/dashboard/trainer/trainers/${trainerId}/${tab}`;
  },

  cloneTrainer: (trainerId) => {
    return `${railsBaseUrl}/dashboard/trainer/trainers/new?copiedFromTrainerId=${trainerId}`;
  },

  clients: (trainerId) => {
    return `${railsBaseUrl}/dashboard/clients/?subTrainerId=${trainerId}`;
  },

  connectionsUrl: () => {
    return `${baseUrl}/connections?nochrome=true`;
  },
};
